import React, { useEffect, useState } from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Container,
  Flex,
  HStack,
  SimpleGrid,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Tfoot,
  Th,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react";
import PageHeader from "../components/PageHeader";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { getActiveSeason, getMlbLeaderboard, getMlbPool } from "../api/mlbHrDerby";

const TOP_BAR_BLUE = "#00005f";
const PAGE_BLUE    = "#00007f";

export default function MlbHrDerbyRostersView() {
  const { user, logout } = useAuth();
  const navigate         = useNavigate();
  const { poolId }       = useParams();
  const numericPoolId    = Number(poolId);

  const username = user?.username || "Player";

  const [leaderboard, setLeaderboard] = useState([]);
  const [loadingPage, setLoadingPage] = useState(true);
  const [pageError,   setPageError]   = useState("");

  useEffect(() => {
    async function loadPage() {
      if (!numericPoolId || Number.isNaN(numericPoolId)) {
        setPageError("Invalid pool.");
        setLoadingPage(false);
        return;
      }
      try {
        const season = await getActiveSeason();
        // The pool fetch's result is unused, but the call is kept: it rejects
        // for non-members, so it doubles as the page's access check.
        const [lbData] = await Promise.all([
          getMlbLeaderboard(season.id, numericPoolId),
          getMlbPool(season.id, numericPoolId),
        ]);
        setLeaderboard(lbData.leaderboard || []);
      } catch (err) {
        const msg = err?.response?.data?.detail || err?.message || "Unable to load page.";
        setPageError(typeof msg === "string" ? msg : "Unable to load page.");
      } finally {
        setLoadingPage(false);
      }
    }
    loadPage();
  }, [numericPoolId]);

  function isMyEntry(entry) {
    return (entry.username || "").trim().toLowerCase() === username.trim().toLowerCase();
  }

  function handleLogout() {
    logout();
    navigate("/login", { replace: true });
  }

  if (loadingPage) {
    return (
      <Box minH="100vh" bg={PAGE_BLUE} color="white">
        <Flex minH="100vh" align="center" justify="center" direction="column" gap={4}>
          <Spinner size="xl" thickness="4px" />
          <Text>Loading rosters...</Text>
        </Flex>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg={PAGE_BLUE} color="white">

      <PageHeader
        title="MLB HR DERBY"
        onBack={() => navigate("/mlb-hr-derby")}
        user={user}
        onLogout={handleLogout}
      />

      {/* ── Page Body ── */}
      <Container maxW="container.xl" py={8}>
        {pageError && (
          <Alert status="error" borderRadius="md" mb={6}>
            <AlertIcon />{pageError}
          </Alert>
        )}

        {/* Action buttons */}
        <HStack mb={6} spacing={3}>
          <Button
            bg="white"
            color={TOP_BAR_BLUE}
            onClick={() => navigate(`/mlb-hr-derby/current/${numericPoolId}`)}
            _hover={{ bg: "gray.100" }}
          >
            Leaderboard View
          </Button>
          <Button
            bg="red.600"
            color="white"
            onClick={() => navigate(`/mlb-hr-derby/current/${numericPoolId}/swap`)}
            _hover={{ bg: "red.700" }}
          >
            Manage Swap
          </Button>
        </HStack>

        {/* ── Roster cards grid ── */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4 }} spacing={5}>
          {leaderboard.map((entry) => {
            const isMe = isMyEntry(entry);
            return (
              <Box
                key={entry.roster_id}
                bg={isMe ? "yellow.50" : "white"}
                color="black"
                border={`3px solid ${isMe ? "#b7791f" : TOP_BAR_BLUE}`}
                borderRadius="lg"
                boxShadow={isMe ? "0 0 0 2px #b7791f" : "lg"}
                p={4}
              >
                <VStack align="stretch" spacing={2}>
                  <HStack spacing={2}>
                    <Badge
                      bg={TOP_BAR_BLUE}
                      color="white"
                      fontSize="xs"
                      px={2}
                      borderRadius="sm"
                    >
                      #{entry.rank}
                    </Badge>
                    {entry.swap_used && (
                      <Badge colorScheme="purple" fontSize="2xs">SWAP</Badge>
                    )}
                  </HStack>

                  <Text
                    fontWeight="bold"
                    fontSize="md"
                    color={TOP_BAR_BLUE}
                    noOfLines={1}
                    title={entry.team_name}
                  >
                    {entry.team_name}
                  </Text>

                  <Table size="xs" variant="simple">
                    <Thead>
                      <Tr>
                        <Th px={1} fontSize="10px">Player</Th>
                        <Th px={1} fontSize="10px" isNumeric>2025</Th>
                        <Th px={1} fontSize="10px" isNumeric>2026</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {(entry.players || []).map((p) => (
                        <Tr key={p.player_id} opacity={p.is_active ? 1 : 0.45}>
                          <Td px={1} fontSize="xs">
                            {p.name}
                            {!p.is_active && (
                              <Badge ml={1} colorScheme="gray" fontSize="2xs">OUT</Badge>
                            )}
                            {p.is_swap_addition && (
                              <Badge ml={1} colorScheme="green" fontSize="2xs">IN</Badge>
                            )}
                          </Td>
                          <Td px={1} fontSize="xs" isNumeric>{p.prev_hrs}</Td>
                          <Td px={1} fontSize="xs" isNumeric fontWeight="bold">{p.curr_hrs}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                    <Tfoot>
                      <Tr bg="gray.100">
                        <Td px={1} fontSize="xs" fontWeight="bold">Total</Td>
                        <Td px={1} fontSize="xs" isNumeric fontWeight="bold">
                          {/* Active players only, matching the current-season
                              total from score_roster(), which filters on
                              is_active = true. */}
                          {(entry.players || [])
                            .filter((p) => p.is_active)
                            .reduce((s, p) => s + (p.prev_hrs || 0), 0)}
                        </Td>
                        <Td px={1} fontSize="xs" isNumeric fontWeight="bold" color={TOP_BAR_BLUE}>
                          {entry.total_hrs}
                        </Td>
                      </Tr>
                    </Tfoot>
                  </Table>
                </VStack>
              </Box>
            );
          })}
        </SimpleGrid>
      </Container>
    </Box>
  );
}
